import React from 'react';
import { AddressFind, LandingZone, Order } from '@m4a3/api-client';
import { OrderCard } from './OrderCard';
import { Box, Typography } from '@mui/material';
import { groupOrdersByLandingZoneAndPad } from '../../../utils/orderUtils';
import { useSelector } from 'react-redux';
/**
 * Props interface for the OrderList component
 */
interface OrderListProps {
  orders: Order[];
  aircraftData?: Record<string, any>;
  groupOrdersByLandingZone?: boolean;
  showPadGrouping?: boolean; // Controls whether to show gray boxes around pads
  landingZones: LandingZone[];
  pads: any[];
  onClickInvalidateDropLocation: (orderId: string, locationId: string) => void;
  onClickShowSuggestedDropModalForOrder: (orderId: string) => void;
  onOpenRouteConfirmation?: (order: Order) => void;
  updateLandingZoneForOrder: (orderId: string, landingZoneId: string) => void;
  openSuggestModal?: () => void;
  onAcceptOrder?: (order: Order) => void;
}

export const OrderList = (props: OrderListProps) => {
  const { pads } = props;

  // If not grouping by landing zone, render the original flat list
  if (!props.groupOrdersByLandingZone) {
    return (
      <>
        {props?.orders.map((o: Order) => (
          <div key={o.id} style={{marginBottom: 16}}>
            <OrderCard 
              order={o} 
              aircraftData={props.aircraftData}
              landingZones={props.landingZones}
              pads={pads}
              openSuggestModal={props.openSuggestModal}
              onClickShowSuggestedDropModalForOrder={props.onClickShowSuggestedDropModalForOrder} 
              onClickInvalidateDropLocation={props.onClickInvalidateDropLocation}
              onOpenRouteConfirmation={props.onOpenRouteConfirmation}
              updateLandingZoneForOrder={props.updateLandingZoneForOrder}
              onAcceptOrder={props.onAcceptOrder}
            />
          </div>
        ))}
      </>
    );
  }

  // Group orders by landing zone and then by Pad
  const landingZones = groupOrdersByLandingZoneAndPad(props.orders, pads);

  // Sort orders within each pad by status then storePreparedDate
  const sortOrdersByStatusAndTime = (a: Order, b: Order) => {
    const statusOrder = ['RECEIVED_FROM_STORE', 'READY', 'DISPATCHED', 'DELIVERED', 'COMPLETE'];
    const aStatusIndex = statusOrder.indexOf(a.status);
    const bStatusIndex = statusOrder.indexOf(b.status);
    
    if (aStatusIndex !== bStatusIndex && aStatusIndex !== -1 && bStatusIndex !== -1) {
      return aStatusIndex - bStatusIndex;
    }
    
    const aTime = a.storePreparedDate ? new Date(a.storePreparedDate).getTime() : 0;
    const bTime = b.storePreparedDate ? new Date(b.storePreparedDate).getTime() : 0;
    return aTime - bTime;
  };

  // Render the hierarchical view with Landing Zone → Pad → Orders structure
  return (
    <Box display={'flex'} flexDirection="column">
      {/* Map through each landing zone */}
      {landingZones.map((landingZone) => {
        // Sort pads alphabetically, with "No Pad" always last
        const sortedPads = [...landingZone.pads].sort((a, b) => {
          if (!a.id) return 1; // No Pad goes to end
          if (!b.id) return -1;
          return (a.name || '').localeCompare(b.name || '');
        });

        return (
          <Box
            key={`landing-zone-${landingZone.id}`}
            display={'flex'}
            flexDirection="column"
            mb={3}
          >
            {/* Landing Zone Header */}
            <Box display="flex" py={2}>
              <h3 className={'text-lg font-semibold text-gray-800'}>
                {landingZone.name}
              </h3>
            </Box>
            
            {/* Pads within this Landing Zone */}
            {sortedPads.map((pad) => {
              // Sort orders within this pad
              const sortedOrders = [...pad.orders].sort(sortOrdersByStatusAndTime);
              
              return (
                <Box key={`pad-${landingZone.id}-${pad.id}`} display={'flex'} flexDirection="column" mb={2}>
                  {/* Show gray pad boxes only if showPadGrouping is true AND pad has ID */}
                  {props.showPadGrouping && pad.id ? (
                    <Box 
                      sx={{ 
                        backgroundColor: '#EAECF0',
                        borderRadius: 2,
                        padding: '12px',
                        mb: 2
                      }}
                    >
                      {/* Pad Name Header */}
                      <Typography variant="h6" style={{ fontSize: 14, fontWeight: 600, color: '#14142B', marginBottom: 12 }}>
                        {pad.name}
                      </Typography>
                      
                      {/* Orders in this pad */}
                      <Box display={'flex'} flexDirection="column" gap={'12px'}>
                        {sortedOrders.map(order => (
                          <OrderCard 
                            key={`${order.id}-card`}
                            order={order} 
                            aircraftData={props.aircraftData}
                            landingZones={props.landingZones}
                            pads={pads}
                            openSuggestModal={props.openSuggestModal}
                            onClickShowSuggestedDropModalForOrder={props.onClickShowSuggestedDropModalForOrder} 
                            onClickInvalidateDropLocation={props.onClickInvalidateDropLocation}
                            onOpenRouteConfirmation={props.onOpenRouteConfirmation}
                            updateLandingZoneForOrder={props.updateLandingZoneForOrder}
                            onAcceptOrder={props.onAcceptOrder}
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    // No pad grouping - render orders directly (ungrouped)
                    <Box display={'flex'} flexDirection="column" gap={'12px'}>
                      {sortedOrders.map(order => (
                        <OrderCard 
                          key={`${order.id}-card`}
                          order={order} 
                          aircraftData={props.aircraftData}
                          landingZones={props.landingZones}
                          pads={pads}
                          openSuggestModal={props.openSuggestModal}
                          onClickShowSuggestedDropModalForOrder={props.onClickShowSuggestedDropModalForOrder} 
                          onClickInvalidateDropLocation={props.onClickInvalidateDropLocation}
                          onOpenRouteConfirmation={props.onOpenRouteConfirmation}
                          updateLandingZoneForOrder={props.updateLandingZoneForOrder}
                          onAcceptOrder={props.onAcceptOrder}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};